import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Sparkles,
  PlusCircle,
  Library,
  BookOpen,
  Gamepad2,
  Users,
  Target,
  Loader2,
  CreditCard,
  ArrowRight,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, color, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.06 }}
    className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm"
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-5 h-5" />
    </div>
    <p className="mt-4 font-heading text-3xl font-black text-brand-ink">{value}</p>
    <p className="text-sm text-brand-muted mt-1">{label}</p>
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const renewalDate = stats?.next_renewal
    ? new Date(stats.next_renewal).toLocaleDateString("pt-BR")
    : "—";
  const aiPct = stats?.ai_quota ? Math.min((stats.ai_used / stats.ai_quota) * 100, 100) : 0;

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">
            Olá, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-brand-muted mt-1">Vamos criar algo incrível hoje?</p>
        </div>
        <Link to="/quiz/ia">
          <Button className="rounded-full h-12 px-6 bg-brand-purple hover:bg-purple-700 gap-2 hover:scale-105 transition-transform" data-testid="dashboard-cta-ia">
            <Sparkles className="w-5 h-5" /> Criar quiz com IA
          </Button>
        </Link>
      </div>

      {stats?.plan_status === "trial_expired" && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-2xl p-5">
          <div>
            <p className="font-heading font-bold text-red-700">Seu período gratuito terminou</p>
            <p className="text-sm text-red-600">Escolha uma assinatura ou compre um quiz avulso para continuar criando com IA e jogos ao vivo.</p>
          </div>
          <Link to="/assinatura">
            <Button className="rounded-full bg-red-500 hover:bg-red-600 shrink-0" data-testid="trial-expired-cta">Ver planos</Button>
          </Link>
        </div>
      )}

      {/* Quick actions */}
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        {[
          { to: "/quiz/ia", icon: Sparkles, label: "Criar com IA", desc: "PDF, link ou texto", color: "from-brand-purple to-brand-blue" },
          { to: "/quiz/novo", icon: PlusCircle, label: "Novo quiz manual", desc: "Do zero, no editor", color: "from-brand-blue to-brand-green" },
          { to: "/quizzes", icon: Library, label: "Meus quizzes", desc: "Biblioteca pessoal", color: "from-brand-yellow to-brand-purple" },
        ].map((a) => {
          const Icon = a.icon;
          return (
            <Link key={a.to} to={a.to} data-testid={`quick-${a.to.replace(/\//g, "-")}`}>
              <div className="group bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${a.color} flex items-center justify-center text-white`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-bold text-brand-ink">{a.label}</p>
                  <p className="text-xs text-brand-muted">{a.desc}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-blue group-hover:translate-x-1 transition-all" />
              </div>
            </Link>
          );
        })}
      </div>

      {!stats ? (
        <div className="mt-10 flex justify-center">
          <Loader2 className="w-7 h-7 animate-spin text-brand-blue" />
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard i={0} icon={BookOpen} label="Quizzes criados" value={stats.total_quizzes} color="text-brand-blue bg-blue-50" />
            <StatCard i={1} icon={Gamepad2} label="Jogos realizados" value={stats.total_games} color="text-brand-purple bg-purple-50" />
            <StatCard i={2} icon={Users} label="Participantes" value={stats.total_participants} color="text-brand-green bg-emerald-50" />
            <StatCard i={3} icon={Target} label="Média de acertos" value={`${stats.avg_accuracy}%`} color="text-brand-yellow bg-amber-50" />
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            {/* AI usage */}
            <div className="lg:col-span-2 bg-white rounded-2xl p-7 border border-slate-100 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-brand-purple" />
                  <h3 className="font-heading font-bold text-brand-ink">Consumo mensal de IA</h3>
                </div>
                <span className="text-sm font-semibold text-brand-ink">
                  {stats.ai_used} de {stats.ai_quota} questões
                </span>
              </div>
              <Progress value={aiPct} className="mt-4 h-3" />
              <p className="mt-3 text-sm text-brand-muted">
                {stats.ai_quota - stats.ai_used > 0
                  ? `Você ainda pode gerar ${stats.ai_quota - stats.ai_used} questões com IA neste mês.`
                  : "Você atingiu o limite mensal de IA. Aguarde a renovação ou mude de plano."}
              </p>
            </div>

            {/* Plan */}
            <div className="bg-gradient-to-br from-brand-blue to-brand-purple rounded-2xl p-7 text-white shadow-lg">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                <h3 className="font-heading font-bold">Plano atual</h3>
              </div>
              <p className="mt-4 font-heading text-2xl font-black">{stats.plan_name}</p>
              <p className="mt-1 text-white/80 text-sm">
                {stats.plan_status === "trial" ? "Acesso gratuito até" : "Próxima renovação"}: {renewalDate}
              </p>
              <Link to="/assinatura">
                <Button className="mt-5 w-full rounded-full bg-white text-brand-blue hover:bg-slate-100 font-semibold" data-testid="dashboard-plan-btn">
                  Gerenciar assinatura
                </Button>
              </Link>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
}
